import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaUpload, FaPlay, FaPause, FaDownload, FaMagic, FaArrowLeft, FaCog } from 'react-icons/fa';
import GIF from 'gif.js';

const InvisibleLooper = ({ onBack }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [videoLoadError, setVideoLoadError] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState('');
  
  // Analysis results
  const [motionAnalysis, setMotionAnalysis] = useState([]);
  const [stillSegments, setStillSegments] = useState([]);
  
  // Processing settings
  const [settings, setSettings] = useState({
    stillThreshold: 0.3, // How still a segment needs to be (0-1)
    stretchFactor: 3.0, // How much to stretch still segments
    boomerangBlend: 0.0, // How much to blend the boomerang transition (0-1) - disabled for clean loops
    motionSensitivity: 0.5, // Motion detection sensitivity
    minStillDuration: 0.5, // Minimum duration for a still segment (seconds)
    forwardVariation: 0.2, // How much variation in forward pass
    reverseVariation: 0.4, // How much variation in reverse pass
  });
  
  // Generated content
  const [processedFrames, setProcessedFrames] = useState([]);
  const [finalVideo, setFinalVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showEffectOverlay, setShowEffectOverlay] = useState(true);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Set up video when we reach step 2
  

  // Video setup for step 2
  useEffect(() => {
    if (currentStep === 2 && selectedFile && videoRef.current) {
      setIsVideoLoading(true);
      setVideoLoadError(null);
      
      const video = videoRef.current;
      const videoUrl = URL.createObjectURL(selectedFile);
      
      const handleLoaded = () => {
        setIsVideoLoading(false);
        setVideoLoadError(null);
      };
      
      const handleError = (e) => {
        console.error('❌ Video loading failed:', e);
        setIsVideoLoading(false);
        setVideoLoadError('Failed to load video file');
      };
      
      // Set up event listeners
      video.addEventListener('loadeddata', handleLoaded);
      video.addEventListener('error', handleError);
      
      // Set video source and load
      video.src = videoUrl;
      
      // Cleanup function
      return () => {
        video.removeEventListener('loadeddata', handleLoaded);
        video.removeEventListener('error', handleError);
        URL.revokeObjectURL(videoUrl);
      };
    }
  }, [currentStep, selectedFile]);

  // Motion Analysis Functions
  const calculateFrameDifference = (imageData1, imageData2) => {
    if (!imageData1 || !imageData2) return 0;
    
    const data1 = imageData1.data;
    const data2 = imageData2.data;
    let totalDiff = 0;
    let pixelCount = 0;
    
    // Sample every 4th pixel for performance
    for (let i = 0; i < data1.length; i += 16) {
      const r1 = data1[i], g1 = data1[i + 1], b1 = data1[i + 2];
      const r2 = data2[i], g2 = data2[i + 1], b2 = data2[i + 2];
      
      // Calculate luminance difference
      const lum1 = 0.299 * r1 + 0.587 * g1 + 0.114 * b1;
      const lum2 = 0.299 * r2 + 0.587 * g2 + 0.114 * b2;
      
      totalDiff += Math.abs(lum1 - lum2);
      pixelCount++;
    }
    
    return pixelCount > 0 ? totalDiff / pixelCount : 0;
  };

  const analyzeVideoMotion = async (video) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      throw new Error('Canvas not available for motion analysis');
    }
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    
    // Use reduced resolution for faster processing
    const width = 160;
    const height = 90;
    canvas.width = width;
    canvas.height = height;
    
    const frames = [];
    const motionData = [];
    const frameRate = 15; // Sample at 15fps
    const totalFrames = Math.floor(video.duration * frameRate);
    
    setProcessingStage('Analyzing video motion patterns');
    
    // Extract frames and calculate motion
    for (let i = 0; i < totalFrames; i++) {
      const time = i / frameRate;
      video.currentTime = time;
      
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error(`Video seek timeout at ${time.toFixed(2)}s`));
        }, 2000);
        
        video.onseeked = () => {
          clearTimeout(timeout);
          
                      try {
              ctx.drawImage(video, 0, 0, width, height);
              const imageData = ctx.getImageData(0, 0, width, height);
              
              frames.push({
                time,
                imageData: imageData,
                index: i
              });
              
              if (i > 0) {
                const motion = calculateFrameDifference(frames[i-1].imageData, imageData);
                motionData.push({
                  time,
                  motion,
                  index: i,
                  frameIndex: i
                });
              }
              
              setProcessingProgress((i / totalFrames) * 30);
              
              // Update preview every few frames to show progress
              if (i % 3 === 0) {
                // The small preview canvas already shows this frame from the drawImage above
                // This creates a live preview effect during analysis
              }
              
              resolve();
          } catch (err) {
            reject(err);
          }
        };
        
        video.onerror = () => {
          clearTimeout(timeout);
          reject(new Error(`Video error at ${time.toFixed(2)}s`));
        };
      });
    }
    
    // Smooth motion data
    const smoothedMotion = smoothMotionData(motionData);
    setMotionAnalysis(smoothedMotion);
    
    return { frames, motionData: smoothedMotion };
  };

  const smoothMotionData = (motionData) => {
    const windowSize = 5;
    return motionData.map((item, index) => {
      const start = Math.max(0, index - windowSize);
      const end = Math.min(motionData.length, index + windowSize + 1);
      const window = motionData.slice(start, end);
      const avgMotion = window.reduce((sum, d) => sum + d.motion, 0) / window.length;
      
      return {
        ...item,
        smoothedMotion: avgMotion
      };
    });
  };

  const findStillSegments = (motionData) => {
    const segments = [];
    let currentSegment = null;
    
    motionData.forEach((frame) => {
      const isStill = (frame.smoothedMotion || frame.motion) < settings.stillThreshold * 100;
      
      if (isStill) {
        if (!currentSegment) {
          currentSegment = {
            start: frame.time,
            startIndex: frame.frameIndex,
            motion: frame.smoothedMotion || frame.motion
          };
        }
      } else {
        if (currentSegment) {
          const duration = frame.time - currentSegment.start;
          if (duration >= settings.minStillDuration) {
            segments.push({
              ...currentSegment,
              end: frame.time,
              endIndex: frame.frameIndex,
              duration,
              avgMotion: currentSegment.motion
            });
          }
          currentSegment = null;
        }
      }
    });
    
    return segments;
  };

  const findOptimalLoopPoints = (frames, motionData) => {
    const candidates = [];
    const endFrame = frames[frames.length - 1];
    
    // Find frames similar to the end frame
    for (let i = 0; i < frames.length - 30; i++) {
      const similarity = calculateFrameSimilarity(frames[i].imageData, endFrame.imageData);
      const motion = motionData[i]?.smoothedMotion || 0;
      
      if (similarity > 0.7 && motion < settings.stillThreshold * 100) {
        candidates.push({
          frameIndex: i,
          time: frames[i].time,
          similarity,
          motion,
          score: similarity * 0.7 + (1 - motion / 100) * 0.3
        });
      }
    }
    
    return candidates.sort((a, b) => b.score - a.score).slice(0, 5);
  };

  const calculateFrameSimilarity = (imageData1, imageData2) => {
    const data1 = imageData1.data;
    const data2 = imageData2.data;
    let similarity = 0;
    let pixelCount = 0;
    
    for (let i = 0; i < data1.length; i += 16) {
      const r1 = data1[i], g1 = data1[i + 1], b1 = data1[i + 2];
      const r2 = data2[i], g2 = data2[i + 1], b2 = data2[i + 2];
      
      const diff = Math.abs(r1 - r2) + Math.abs(g1 - g2) + Math.abs(b1 - b2);
      similarity += Math.max(0, 255 - diff) / 255;
      pixelCount++;
    }
    
    return pixelCount > 0 ? similarity / pixelCount : 0;
  };

  // Time Stretching Functions - Improved with smooth transitions
  const createTimeStretchedSequence = (frames, stillSegments, isReverse = false) => {
    console.log(`⏱️ createTimeStretchedSequence (${isReverse ? 'reverse' : 'forward'}) with ${frames.length} frames, ${stillSegments.length} still segments`);
    
    const stretchedFrames = [];
    const transitionZone = 0.3; // 300ms transition zones
    
    for (let frameIndex = 0; frameIndex < frames.length; frameIndex++) {
      const currentFrame = frames[frameIndex];
      const currentTime = currentFrame.time;
      
      // Calculate stretch factor for this frame (smooth transitions)
      const stretchFactor = calculateSmoothStretchFactor(currentTime, stillSegments, transitionZone);
      
      // Always add the original frame
      stretchedFrames.push({
        ...currentFrame,
        stretched: stretchFactor > 1.0,
        stretchType: stretchFactor > 1.5 ? 'still' : (stretchFactor > 1.0 ? 'transition' : 'normal'),
        actualStretch: stretchFactor
      });
      
      // Add interpolated frames for stretching (but only if significant stretch)
      if (stretchFactor > 1.2) {
        const additionalFrames = Math.floor(stretchFactor - 1);
        
        for (let s = 1; s <= additionalFrames; s++) {
          const nextFrameIndex = Math.min(frameIndex + 1, frames.length - 1);
          const nextFrame = frames[nextFrameIndex];
          
          // Create interpolated frame between current and next
          const interpolationRatio = s / (additionalFrames + 1);
          const interpolatedFrame = createInterpolatedFrame(currentFrame, nextFrame, interpolationRatio, s);
          
          stretchedFrames.push({
            ...interpolatedFrame,
            stretched: true,
            stretchIndex: s,
            stretchType: 'interpolated',
            actualStretch: stretchFactor,
            interpolationRatio
          });
        }
      }
    }
    
    console.log(`✅ Smooth time stretch complete (${isReverse ? 'reverse' : 'forward'}): ${stretchedFrames.length} frames`);
    return stretchedFrames;
  };

  // Calculate smooth stretch factor with gradual transitions
  const calculateSmoothStretchFactor = (currentTime, stillSegments, transitionZone) => {
    let maxStretch = 1.0;
    
    stillSegments.forEach(segment => {
      const segmentCenter = (segment.start + segment.end) / 2;
      const segmentRadius = (segment.end - segment.start) / 2;
      const totalRadius = segmentRadius + transitionZone;
      
      const distanceFromCenter = Math.abs(currentTime - segmentCenter);
      
      if (distanceFromCenter <= totalRadius) {
        let localStretch;
        
        if (distanceFromCenter <= segmentRadius) {
          // Inside still segment - full stretch
          localStretch = settings.stretchFactor;
        } else {
          // In transition zone - gradual falloff
          const transitionProgress = (distanceFromCenter - segmentRadius) / transitionZone;
          const smoothTransition = 0.5 * (1 + Math.cos(Math.PI * transitionProgress)); // Smooth cosine falloff
          localStretch = 1.0 + (settings.stretchFactor - 1.0) * smoothTransition;
        }
        
        maxStretch = Math.max(maxStretch, localStretch);
      }
    });
    
    return maxStretch;
  };

  // Create interpolated frame between two frames
  const createInterpolatedFrame = (frame1, frame2, ratio, stretchIndex) => {
    // Simple time interpolation for now
    const timeDiff = frame2.time - frame1.time;
    const interpolatedTime = frame1.time + (timeDiff * ratio);
    
    return {
      ...frame1,
      time: interpolatedTime,
      interpolated: true,
      baseFrame: frame1.index,
      targetFrame: frame2.index,
      interpolationRatio: ratio,
      stretchIndex
    };
  };

  // Visual Effect Overlay Functions
  const renderEffectOverlay = (canvas, currentTime, stillSegments) => {
    if (!showEffectOverlay) return;

    const ctx = canvas.getContext('2d');
    
    // Check if current time is in a still segment
    const currentStillSegment = stillSegments?.find(segment => 
      currentTime >= segment.start && currentTime <= segment.end
    );

    if (currentStillSegment) {
      // Draw overlay indicator for still segment
      ctx.save();
      
      // Semi-transparent green overlay with pulsing effect
      const time = Date.now() / 1000;
      const pulse = 0.15 + 0.1 * Math.sin(time * 2);
      ctx.fillStyle = `rgba(34, 197, 94, ${pulse})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add animated border effect
      const borderPulse = 0.6 + 0.3 * Math.sin(time * 3);
      ctx.strokeStyle = `rgba(34, 197, 94, ${borderPulse})`;
      ctx.lineWidth = 8;
      ctx.strokeRect(4, 4, canvas.width - 8, canvas.height - 8);
      
      // Add text indicator with shadow
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.font = 'bold 32px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('🌊 TIME STRETCH ZONE', canvas.width / 2 + 2, 42);
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.fillText('🌊 TIME STRETCH ZONE', canvas.width / 2, 40);
      
      // Add segment info
      ctx.font = '20px Arial, sans-serif';
      const segmentIndex = stillSegments.indexOf(currentStillSegment);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.fillText(`Enhancement Zone ${segmentIndex + 1}`, canvas.width / 2 + 1, 71);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillText(`Enhancement Zone ${segmentIndex + 1}`, canvas.width / 2, 70);
      
      ctx.restore();
    }
  };

  const addForwardVariation = (frame, stretchIndex) => {
    // Add subtle variations for forward pass
    return {
      ...frame,
      time: frame.time + (stretchIndex * 0.033), // 30fps equivalent
      variation: 'forward',
      variationAmount: settings.forwardVariation
    };
  };

  const addReverseVariation = (frame, stretchIndex) => {
    // Add different variations for reverse pass
    return {
      ...frame,
      time: frame.time + (stretchIndex * 0.033),
      variation: 'reverse',
      variationAmount: settings.reverseVariation
    };
  };

  // Invisible Boomerang Creation - Enhanced with smart cutoff
  const createInvisibleBoomerang = (frames, stillSegments, loopPoints = []) => {
    setProcessingStage('Creating intelligent boomerang with enhancement zone cutoff...');
    
    console.log('🎪 createInvisibleBoomerang called with:');
    console.log('  frames:', frames.length);
    console.log('  stillSegments:', stillSegments.length);
    console.log('  loopPoints:', loopPoints ? loopPoints.length : 0);
    
    const bestLoopPoint = loopPoints && loopPoints.length > 0 ? loopPoints[0] : null;
    if (!bestLoopPoint) {
      console.log('🔄 No optimal loop point found, using smart end-focused boomerang');
      // Fallback to enhanced end-focused boomerang
      const result = createSmartEndFocusedBoomerang(frames, stillSegments);
      console.log('🎪 createInvisibleBoomerang returning', result ? result.length : 'undefined', 'frames');
      return result;
    }
    
    // Split video at optimal loop point
    const loopFrameIndex = bestLoopPoint.frameIndex;
    const beforeLoop = frames.slice(0, loopFrameIndex);
    const afterLoop = frames.slice(loopFrameIndex);
    
    // Create forward pass with time stretching
    const forwardPass = createTimeStretchedSequence([...beforeLoop, ...afterLoop], stillSegments, false);
    
    // 🎯 SMART CUTOFF: Find where to end based on last enhancement zone
    const cutoffIndex = findLastEnhancementZoneCutoff(forwardPass, stillSegments);
    const trimmedForwardPass = cutoffIndex > 0 ? forwardPass.slice(0, cutoffIndex) : forwardPass;
    
    console.log(`✂️ Trimmed forward pass: ${trimmedForwardPass.length} frames (was ${forwardPass.length})`);
    
    // Create reverse pass with the trimmed sequence
    const reverseSegment = [...trimmedForwardPass].reverse();
    const reversePass = createTimeStretchedSequence(reverseSegment, stillSegments, true);
    
    // Also trim the reverse pass to match
    const trimmedReversePass = reversePass.slice(0, cutoffIndex);
    
    // Combine sequences directly without transitions for clean perfect loop
    const finalSequence = [
      ...trimmedForwardPass,
      ...trimmedReversePass.slice(1) // Skip first frame to avoid duplication
    ];
    
    console.log(`🎪 Smart boomerang complete: ${finalSequence.length} total frames`);
    return finalSequence;
  };

  const createSmartEndFocusedBoomerang = (frames, stillSegments) => {
    console.log('🔄 createSmartEndFocusedBoomerang - with enhancement zone cutoff');
    
    // Focus on the last 40% of the video for end-of-video effects
    const endFocusPoint = Math.floor(frames.length * 0.6);
    const earlyFrames = frames.slice(0, endFocusPoint);
    const endFrames = frames.slice(endFocusPoint);
    
    console.log(`📽️ Split video: Early ${earlyFrames.length} frames, End ${endFrames.length} frames`);
    
    // Forward pass: Normal early, stretched end
    const forwardEarly = earlyFrames; // No stretching for early frames
    const forwardEnd = createTimeStretchedSequence(endFrames, stillSegments, false);
    const fullForwardPass = [...forwardEarly, ...forwardEnd];
    
    // 🎯 SMART CUTOFF: Apply enhancement zone cutoff to forward pass
    const cutoffIndex = findLastEnhancementZoneCutoff(fullForwardPass, stillSegments);
    const trimmedForwardPass = cutoffIndex > 0 ? fullForwardPass.slice(0, cutoffIndex) : fullForwardPass;
    
    console.log(`✂️ Smart cutoff applied: ${trimmedForwardPass.length} frames (was ${fullForwardPass.length})`);
    
    // Create reverse pass from the trimmed forward pass
    const reverseFrames = [...trimmedForwardPass].reverse();
    
    // Apply different reverse processing to the trimmed sequence
    const reversePass = createEndReverseStretch(reverseFrames.slice(Math.max(0, reverseFrames.length - endFrames.length)));
    
    const finalResult = [...trimmedForwardPass, ...reversePass];
    
    console.log('🎪 Smart end-focused boomerang complete:', finalResult.length, 'total frames');
    console.log('🔍 Breakdown: Forward', trimmedForwardPass.length, '+ Reverse', reversePass.length, '= Total', finalResult.length);
    
    if (!finalResult || finalResult.length === 0) {
      console.error('❌ createSmartEndFocusedBoomerang produced empty result!');
      console.error('  trimmedForwardPass:', trimmedForwardPass ? trimmedForwardPass.length : 'undefined');
      console.error('  reversePass:', reversePass ? reversePass.length : 'undefined');
    }
    
    return finalResult;
  };

  // Special stretching for reverse direction - focuses on extending the transition
  const createEndReverseStretch = (endFrames) => {
    console.log('🔄 Creating special reverse stretch for', endFrames.length, 'end frames');
    
    const stretchedFrames = [];
    
    endFrames.forEach((frame, index) => {
      // Add original frame
      stretchedFrames.push({
        ...frame,
        stretched: false,
        reverseEnd: true
      });
      
      // Heavy stretching for first few frames (which were the very end of the original video)
      const isTransitionFrame = index < Math.min(5, endFrames.length * 0.3);
      
      if (isTransitionFrame) {
        // Add multiple stretched frames for smooth end transition
        const stretchCount = 4 - index; // More stretch for earlier frames
        
        for (let s = 0; s < stretchCount; s++) {
          stretchedFrames.push({
            ...frame,
            time: frame.time + (s * 0.033),
            stretched: true,
            stretchIndex: s,
            reverseEnd: true,
            transitionFrame: true
          });
        }
      }
    });
    
    console.log(`✅ Reverse end stretch complete: ${stretchedFrames.length} frames from ${endFrames.length} original`);
    return stretchedFrames;
  };

  const createBlendedTransition = (frame1, frame2, blendAmount) => {
    const transitionFrames = [];
    const steps = 5; // Number of blend frames
    
    for (let i = 0; i < steps; i++) {
      const alpha = (i / (steps - 1)) * blendAmount;
      transitionFrames.push({
        ...frame1,
        blendWith: frame2,
        blendAlpha: alpha,
        transitionFrame: true,
        time: frame1.time + (i * 0.033)
      });
    }
    
    return transitionFrames;
  };

  const createLoopTransition = (endFrame, startFrame) => {
    // Create seamless loop transition
    return createBlendedTransition(endFrame, startFrame, settings.boomerangBlend);
  };

  // Main Processing Function
  const processVideo = async () => {
    if (!videoRef.current) {
      console.error('❌ Video ref not available');
      alert('Video not loaded. Please wait for the video to load completely.');
      return;
    }
    
    if (!canvasRef.current) {
      console.error('❌ Canvas ref not available');
      alert('Canvas not ready. Please refresh and try again.');
      return;
    }
    
    setIsLoading(true);
    setProcessingProgress(0);
    
    try {
      const video = videoRef.current;
      console.log('📊 Video details:', {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        readyState: video.readyState
      });
      
      // Step 1: Analyze motion
      setProcessingStage('Analyzing video motion patterns');
      const { frames, motionData } = await analyzeVideoMotion(video);
      setProcessingProgress(30);
      
      // Step 2: Find still segments
      setProcessingStage('Detecting still moments for enhancement');
      const stillSegs = findStillSegments(motionData);
      
      // If no segments found, create demo segments for effect demonstration
      let finalStillSegs = stillSegs;
      if (stillSegs.length === 0) {
        const videoDuration = video.duration;
        finalStillSegs = [
          {
            start: videoDuration * 0.2,
            end: videoDuration * 0.4,
            duration: videoDuration * 0.2,
            avgMotion: 0.1
          },
          {
            start: videoDuration * 0.7,
            end: videoDuration * 0.9,
            duration: videoDuration * 0.2,
            avgMotion: 0.15
          }
        ];
      }
      
      setStillSegments(finalStillSegs);
      setProcessingProgress(50);
      
      // Step 3: Create smart boomerang with cutoff
      setProcessingStage('Creating smart boomerang with enhancement zone cutoff');
      const processedSeq = createInvisibleBoomerang(frames, finalStillSegs, []);
      console.log('🎯 processedSeq result:', processedSeq ? processedSeq.length : 'undefined', 'frames');
      setProcessedFrames(processedSeq);
      setProcessingProgress(90);
      
      // Step 4: Render final video
      setProcessingStage('Finalizing your optimized loop');
      const renderedFrames = await renderFinalVideo(processedSeq);
      setProcessingProgress(100);
      
      setCurrentStep(3);
      
      // Start auto-playback after step change (give UI time to render)
      setTimeout(() => {
        // Priority 1: Use directly returned frames (most reliable)
        if (renderedFrames && renderedFrames.length > 0) {
          setIsPlaying(true);
          const stopFunction = playFramesDirectly(renderedFrames);
          window.currentPlaybackStop = stopFunction;
        }
        // Priority 2: Use state-based finalVideo
        else if (finalVideo && finalVideo.length > 0) {
          setIsPlaying(true);
          const stopFunction = playFramesDirectly(finalVideo);
          window.currentPlaybackStop = stopFunction;
        }
        // Priority 3: Try to generate new frames if we have processed sequence
        else if (processedSeq && processedSeq.length > 0) {
          renderAndPlayPreview(processedSeq);
        }
      }, 1500); // Longer delay to ensure UI is ready
      
    } catch (error) {
      console.error('Processing error:', error);
      console.error('Error stack:', error.stack);
      
      let errorMessage = 'Error processing video. ';
      if (error.message.includes('Canvas')) {
        errorMessage += 'Canvas not ready - please refresh the page and try again.';
      } else if (error.message.includes('video')) {
        errorMessage += 'Video processing failed - try a different video file.';
      } else {
        errorMessage += 'Please try again.';
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
      setProcessingStage('');
    }
  };

  const renderFinalVideo = async (processedSequence) => {
    console.log('🎬 renderFinalVideo called with', processedSequence ? processedSequence.length : 'undefined', 'frames');
    
    if (!processedSequence || processedSequence.length === 0) {
      console.error('❌ renderFinalVideo received empty or undefined processedSequence');
      console.error('  processedSequence:', processedSequence);
      throw new Error('No processed sequence available for rendering');
    }
    
    // Use canvasRef for processing, create previewCanvas if needed
    let canvas = previewCanvasRef.current;
    if (!canvas) {
      console.log('📺 Preview canvas not ready, using processing canvas');
      canvas = canvasRef.current;
    }
    
    if (!canvas) {
      console.error('❌ No canvas available for rendering');
      throw new Error('Canvas not available for rendering');
    }
    
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const video = videoRef.current;
    
    if (!video) {
      console.error('❌ No video element for rendering');
      throw new Error('Video not available for rendering');
    }
    
    canvas.width = video.videoWidth || 960;
    canvas.height = video.videoHeight || 960;
    
    console.log('🎨 Canvas setup:', canvas.width + 'x' + canvas.height);
    
    // Create video frames for playback - use all frames for demo
    const videoFrames = [];
    const maxFrames = Math.min(processedSequence.length, 100); // Reduced for faster demo
    
    console.log('🔄 Processing', maxFrames, 'frames for final video');
    
    for (let i = 0; i < maxFrames; i++) {
      const frame = processedSequence[i];
      
      try {
        // Set video to original frame time
        video.currentTime = frame.time;
        
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            console.warn(`⏰ Timeout at frame ${i}, continuing...`);
            resolve(); // Continue instead of failing
          }, 1000);
          
          video.onseeked = () => {
            clearTimeout(timeout);
            
            try {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              
              // Always draw the frame directly - no blending/fading effects
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              
              const frameData = canvas.toDataURL('image/jpeg', 0.8);
              videoFrames.push(frameData);
              
              if (i % 10 === 0) {
                console.log(`📷 Rendered frame ${i}/${maxFrames}`);
              }
              
              setProcessingProgress(90 + (i / maxFrames) * 10);
              resolve();
            } catch (err) {
              console.error('❌ Error rendering frame', i, err);
              resolve(); // Continue despite errors
            }
          };
          
          video.onerror = () => {
            clearTimeout(timeout);
            console.error('❌ Video error at frame', i);
            resolve(); // Continue despite errors
          };
        });
      } catch (err) {
        console.error('❌ Error processing frame', i, err);
        // Continue processing other frames
      }
    }
    
    console.log('✅ Final video rendering complete:', videoFrames.length, 'frames');
    setFinalVideo(videoFrames);
    
    if (videoFrames.length === 0) {
      console.error('❌ No frames generated - creating fallback frames');
      // Create fallback frames from current video
      const fallbackFrames = [];
      for (let i = 0; i < 10; i++) {
        video.currentTime = i * 0.5; // Sample every 0.5 seconds
        await new Promise(resolve => {
          video.onseeked = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            fallbackFrames.push(canvas.toDataURL('image/jpeg', 0.8));
            resolve();
          };
        });
      }
      console.log('🔄 Generated', fallbackFrames.length, 'fallback frames');
      setFinalVideo(fallbackFrames);
      return fallbackFrames;
    }
    
    // Return the frames directly for immediate use
    return videoFrames;
  };

  // Playback Functions
  const playGeneratedVideo = () => {
    console.log('🎬 playGeneratedVideo called');
    console.log('📊 finalVideo length:', finalVideo?.length || 0);
    console.log('🎨 previewCanvasRef exists:', !!previewCanvasRef.current);
    
    if (!finalVideo || finalVideo.length === 0) {
      console.warn('⚠️ No final video to play - finalVideo is empty or not ready');
      return;
    }
    
    if (!previewCanvasRef.current) {
      console.warn('⚠️ No canvas available for playback');
      return;
    }
    
    // Use the direct playback function with current finalVideo
    setIsPlaying(true);
    const stopFunction = playFramesDirectly(finalVideo);
    window.currentPlaybackStop = stopFunction;
  };

  const stopGeneratedVideo = () => {
    console.log('⏹️ Stopping video playback');
    setIsPlaying(false);
    
    // Call the current playback stop function if available
    if (window.currentPlaybackStop) {
      console.log('🛑 Calling playback stop function');
      window.currentPlaybackStop();
      window.currentPlaybackStop = null;
    }
  };

  // Direct render and play function to avoid state timing issues
  const renderAndPlayPreview = async (processedSequence) => {
    console.log('🎬 renderAndPlayPreview called with', processedSequence.length, 'frames');
    
    // Wait for elements to be available (they might not be ready immediately after step change)
    let attempts = 0;
    const maxAttempts = 10;
    
    while ((!previewCanvasRef.current || !videoRef.current) && attempts < maxAttempts) {
      console.log(`⏳ Waiting for canvas/video elements... attempt ${attempts + 1}/${maxAttempts}`);
      await new Promise(resolve => setTimeout(resolve, 200));
      attempts++;
    }
    
    if (!previewCanvasRef.current || !videoRef.current) {
      console.error('❌ Canvas or video still not available after waiting');
      console.log('  previewCanvasRef.current:', !!previewCanvasRef.current);
      console.log('  videoRef.current:', !!videoRef.current);
      
      // Try fallback with regular canvas
      if (canvasRef.current && videoRef.current) {
        console.log('🔄 Trying fallback with processing canvas');
        return renderWithFallbackCanvas(processedSequence);
      }
      
      return;
    }
    
    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const video = videoRef.current;
    
    canvas.width = video.videoWidth || 960;
    canvas.height = video.videoHeight || 960;
    
    // Create preview frames quickly (sample every 2nd frame for speed)
    const previewFrames = [];
    const maxPreviewFrames = Math.min(50, Math.floor(processedSequence.length / 2));
    
    console.log('🎥 Creating', maxPreviewFrames, 'preview frames');
    
    for (let i = 0; i < maxPreviewFrames; i++) {
      const frameIndex = i * 2; // Sample every 2nd frame
      if (frameIndex >= processedSequence.length) break;
      
      const frame = processedSequence[frameIndex];
      
      try {
        video.currentTime = frame.time;
        
        await new Promise((resolve) => {
          const timeout = setTimeout(resolve, 100); // Quick timeout
          
          video.onseeked = () => {
            clearTimeout(timeout);
            
            try {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              
              const frameData = canvas.toDataURL('image/jpeg', 0.7);
              previewFrames.push(frameData);
            } catch (err) {
              console.warn('Preview frame error:', err);
            }
            
            resolve();
          };
        });
      } catch (err) {
        console.warn('Preview frame', i, 'failed:', err);
      }
    }
    
    console.log('✅ Preview frames ready:', previewFrames.length);
    
    // Update state and start playback
    setFinalVideo(previewFrames);
    
    // Start playback immediately with the frames we just created
    if (previewFrames.length > 0) {
      setIsPlaying(true);
      const stopFunction = playFramesDirectly(previewFrames);
      window.currentPlaybackStop = stopFunction;
    }
  };

    // Play frames directly without state dependency
  const playFramesDirectly = (frames) => {
    console.log('🎬 playFramesDirectly called with', frames.length, 'frames');
    console.log('🔍 Current isPlaying state:', isPlaying);
    
    if (!previewCanvasRef.current) {
      console.error('❌ No canvas for direct playback');
      return;
    }
    
    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');
    let frameIndex = 0;
    let direction = 1; // 1 for forward, -1 for reverse
    let localPlayback = true; // Use local variable instead of state
    
    // Ensure canvas is properly sized and visible
    console.log('🎨 Canvas details:');
    console.log('  Canvas width:', canvas.width);
    console.log('  Canvas height:', canvas.height);
    console.log('  Canvas style display:', canvas.style.display);
    console.log('  Canvas offsetWidth:', canvas.offsetWidth);
    console.log('  Canvas offsetHeight:', canvas.offsetHeight);
    
    // Set canvas size if not set
    if (canvas.width === 0 || canvas.height === 0) {
      console.log('🔧 Setting canvas dimensions to 960x960');
      canvas.width = 960;
      canvas.height = 960;
    }
    
    console.log('🎯 Starting boomerang playback with local control');
    
    const actualPlayFrame = () => {
      if (!localPlayback) {
        console.log('⏹️ Local playback stopped at frame', frameIndex);
        setIsPlaying(false);
        return;
      }
      
      // Ensure we have valid frame data
      if (!frames[frameIndex] || frameIndex < 0 || frameIndex >= frames.length) {
        console.error('❌ Invalid frame index', frameIndex, 'of', frames.length);
        frameIndex = 0;
        direction = 1;
        setTimeout(actualPlayFrame, 100);
        return;
      }
      
      const img = new Image();
      
      img.onload = () => {
        if (!localPlayback) {
          console.log('⏹️ Playback stopped during frame load');
          return;
        }
        
        try {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Update current playback time for overlay
          if (frames[frameIndex] && frames[frameIndex].time !== undefined) {
            setCurrentPlaybackTime(frames[frameIndex].time);
          } else {
            // For boomerang frames without time info, simulate progress
            const videoDuration = videoRef.current?.duration || 5;
            const simulatedTime = (frameIndex / frames.length) * videoDuration;
            setCurrentPlaybackTime(simulatedTime);
          }
          
          // Always try to render effect overlay
          const timeForOverlay = frames[frameIndex]?.time || (frameIndex / frames.length) * (videoRef.current?.duration || 5);
          renderEffectOverlay(canvas, timeForOverlay, stillSegments);
          
          // Only log occasionally to avoid console spam
          if (frameIndex % 15 === 0) {
            const directionStr = direction > 0 ? 'Forward' : 'Reverse';
            console.log(`🖼️ ${directionStr} frame ${frameIndex}/${frames.length - 1}`);
          }
          
          // Move to next frame with boomerang logic
          frameIndex += direction;
          
          // Check boundaries and reverse direction for boomerang effect
          if (frameIndex >= frames.length - 1) {
            direction = -1; // Start going reverse
            frameIndex = frames.length - 1; // Stay at last frame
            console.log('🔄 Boomerang: Switching to reverse');
          } else if (frameIndex <= 0) {
            direction = 1; // Start going forward
            frameIndex = 0; // Stay at first frame
            console.log('🔄 Boomerang: Switching to forward');
          }
          
          setTimeout(actualPlayFrame, 1000 / 15); // 15 FPS for smooth boomerang
        } catch (err) {
          console.error('❌ Error drawing frame', frameIndex, err);
          setTimeout(actualPlayFrame, 100);
        }
      };
      
      img.onerror = () => {
        console.error('❌ Frame', frameIndex, 'failed to load');
        setTimeout(actualPlayFrame, 100);
      };
      
      // Set the image source
      img.src = frames[frameIndex];
    };
    
    // Start playback immediately
    actualPlayFrame();
    
    // Return stop function for manual control
    return () => {
      localPlayback = false;
      setIsPlaying(false);
      console.log('⏹️ Manual stop called');
    };
  };

  // Fallback rendering with processing canvas
  const renderWithFallbackCanvas = async (processedSequence) => {
    console.log('🔄 renderWithFallbackCanvas called with', processedSequence.length, 'frames');
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const video = videoRef.current;
    
    canvas.width = video.videoWidth || 960;
    canvas.height = video.videoHeight || 960;
    
    // Create quick preview frames
    const previewFrames = [];
    const maxPreviewFrames = Math.min(30, Math.floor(processedSequence.length / 3));
    
    console.log('🎥 Creating', maxPreviewFrames, 'fallback frames');
    
    for (let i = 0; i < maxPreviewFrames; i++) {
      const frameIndex = i * 3; // Sample every 3rd frame for speed
      if (frameIndex >= processedSequence.length) break;
      
      const frame = processedSequence[frameIndex];
      
      try {
        video.currentTime = frame.time;
        
        await new Promise((resolve) => {
          const timeout = setTimeout(resolve, 50); // Very quick timeout
          
          video.onseeked = () => {
            clearTimeout(timeout);
            
            try {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              
              const frameData = canvas.toDataURL('image/jpeg', 0.6);
              previewFrames.push(frameData);
            } catch (err) {
              console.warn('Fallback frame error:', err);
            }
            
            resolve();
          };
        });
      } catch (err) {
        console.warn('Fallback frame', i, 'failed:', err);
      }
    }
    
    console.log('✅ Fallback frames ready:', previewFrames.length);
    
    // Update state
    setFinalVideo(previewFrames);
    
    // Try to use preview canvas if it becomes available, otherwise show message
    if (previewCanvasRef.current) {
      setIsPlaying(true);
      const stopFunction = playFramesDirectly(previewFrames);
      window.currentPlaybackStop = stopFunction;
    } else {
      console.log('💡 Fallback complete - preview canvas still not ready, but frames are generated');
    }
  };

  // Find the last enhancement zone cutoff point - Conservative approach
  const findLastEnhancementZoneCutoff = (stretchedFrames, stillSegments) => {
    if (!stillSegments || stillSegments.length === 0) {
      console.log('🔄 No enhancement zones found, using 95% of video (trim only tail)');
      return Math.floor(stretchedFrames.length * 0.95); // Only trim last 5% if no zones
    }
    
    // Find the last (rightmost) enhancement zone
    const lastEnhancementZone = stillSegments.reduce((latest, current) => {
      return current.end > latest.end ? current : latest;
    });
    
    console.log(`🎯 Last enhancement zone: ${lastEnhancementZone.start.toFixed(2)}s - ${lastEnhancementZone.end.toFixed(2)}s`);
    
    // Conservative cutoff - longer buffer to maintain boomerang length
    const bufferTime = 0.5; // 500ms buffer after the last zone (more conservative)
    const cutoffTime = lastEnhancementZone.end + bufferTime;
    
    // Find the last frame that should be included
    let cutoffIndex = stretchedFrames.length;
    
    for (let i = 0; i < stretchedFrames.length; i++) {
      const frame = stretchedFrames[i];
      
      // For stretched frames, we need to look at the original time
      const frameTime = frame.time || 0;
      
      if (frameTime > cutoffTime) {
        cutoffIndex = i;
        console.log(`✂️ Conservative cutting at frame ${i}/${stretchedFrames.length} (time: ${frameTime.toFixed(2)}s, cutoff: ${cutoffTime.toFixed(2)}s)`);
        break;
      }
    }
    
    // Much more conservative minimum - keep at least 85% of frames
    const minFrames = Math.floor(stretchedFrames.length * 0.85);
    if (cutoffIndex < minFrames) {
      console.log(`⚠️ Cutoff too aggressive (${cutoffIndex}), using conservative minimum ${minFrames} frames`);
      cutoffIndex = minFrames;
    }
    
    console.log(`🎬 Conservative cutoff: ${cutoffIndex}/${stretchedFrames.length} frames (${((cutoffIndex/stretchedFrames.length)*100).toFixed(1)}% of original)`);
    return cutoffIndex;
  };

  // Export Functions
  const exportAsGIF = async () => {
    if (!processedFrames || processedFrames.length === 0) {
      alert('No video data available to export. Please process a video first.');
      return;
    }

    console.log('🔍 Export Debug Info:');
    console.log('  processedFrames.length:', processedFrames.length);
    console.log('  finalVideo.length:', finalVideo?.length || 0);
    console.log('  Using complete processedFrames for export');

    setIsExporting(true);
    
    try {
      // Create GIF encoder with optimized settings
      const gif = new GIF({
        workers: 2,
        quality: 10, // Lower = better quality (1-30)
        width: 480,
        height: 480,
        repeat: 0, // 0 = infinite loop
        workerScript: '/gif.worker.js' // Will fallback if not found
      });
      
      // Create a temporary canvas for frame processing
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCanvas.width = 480;
      tempCanvas.height = 480;
      
      // Use ALL processed frames - the complete boomerang sequence
      const framesToUse = processedFrames;
      const frameDelay = 80; // 80ms = 12.5fps
      
      console.log(`🎬 Creating GIF with ALL ${framesToUse.length} frames from complete sequence`);
      
      // Sample frames across entire boomerang to get complete loop
      const maxGifFrames = 40; // Smaller for faster export
      const totalFrames = framesToUse.length;
      let framesToExport = [];
      
      if (totalFrames <= maxGifFrames) {
        framesToExport = [...framesToUse];
      } else {
        // Sample evenly across the ENTIRE boomerang sequence
        for (let i = 0; i < maxGifFrames; i++) {
          const frameIndex = Math.floor((i / (maxGifFrames - 1)) * (totalFrames - 1));
          framesToExport.push(framesToUse[frameIndex]);
        }
      }
      
      console.log(`🎬 Sampling ${framesToExport.length} frames across complete ${framesToUse.length} frame boomerang`);
      
      // Get video element for frame generation
      const video = videoRef.current;
      if (!video) {
        throw new Error('Video element not available');
      }
      
      let successfulFrames = 0;
      
      for (let i = 0; i < framesToExport.length; i++) {
        const frame = framesToExport[i];
        
        // Validate frame data
        if (!frame || typeof frame.time !== 'number' || isNaN(frame.time)) {
          console.warn(`⚠️ Skipping invalid frame ${i}:`, frame);
          continue;
        }
        
        try {
          console.log(`🔄 Processing frame ${i + 1}/${framesToExport.length} at time ${frame.time.toFixed(2)}s`);
          
          // Update progress manually during frame processing
          const frameProgress = Math.round((i / framesToExport.length) * 80); // 0-80% for frame processing
          setExportProgress(frameProgress);
          
          // Set video to the frame's time
          video.currentTime = frame.time;
          
          await new Promise((resolve) => {
            const timeout = setTimeout(() => {
              console.warn(`⏰ Timeout at frame ${i}, skipping...`);
              resolve();
            }, 1000); // Shorter timeout
            
            const cleanup = () => {
              clearTimeout(timeout);
              video.onseeked = null;
              video.onerror = null;
            };
            
            video.onseeked = () => {
              cleanup();
              
              try {
                // Clear canvas and draw current video frame
                tempCtx.clearRect(0, 0, 480, 480);
                tempCtx.drawImage(video, 0, 0, 480, 480);
                
                // Add frame to GIF
                gif.addFrame(tempCanvas, { 
                  delay: frameDelay,
                  copy: true
                });
                
                successfulFrames++;
                console.log(`✅ Added frame ${i + 1} (${successfulFrames} successful)`);
                
                resolve();
              } catch (err) {
                console.error(`❌ Error processing frame ${i}:`, err);
                resolve();
              }
            };
            
            video.onerror = () => {
              cleanup();
              console.error(`❌ Video error at frame ${i}`);
              resolve();
            };
          });
        } catch (err) {
          console.error(`❌ Error with frame ${i}:`, err);
        }
      }
      
      console.log(`📊 Successfully processed ${successfulFrames}/${framesToExport.length} frames`);
      
      // Check if we have enough frames to create a GIF
      if (successfulFrames < 2) {
        throw new Error(`Not enough frames for GIF (only ${successfulFrames} successful frames)`);
      }
      
      // Show progress for GIF rendering phase
      setExportProgress(85);
      console.log(`🚀 Starting GIF render with ${successfulFrames} frames...`);
      
      // Set up timeout for GIF rendering  
      const renderTimeout = setTimeout(() => {
        console.error('❌ GIF render timeout - taking too long');
        setIsExporting(false);
        setExportProgress(0);
        alert('GIF creation timed out. This video may be too complex for GIF export.');
      }, 20000); // 20 second timeout
      
      // Set up GIF event handlers
      gif.on('finished', (blob) => {
        clearTimeout(renderTimeout);
        setExportProgress(100);
        console.log('✅ GIF creation complete!');
        
        try {
          // Download the GIF
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `boomerang-loop-${Date.now()}.gif`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          console.log('📁 GIF downloaded successfully');
          
          // Small delay to show 100% before closing
          setTimeout(() => {
            setIsExporting(false);
            setExportProgress(0);
            setShowExportModal(false);
          }, 1000);
        } catch (downloadError) {
          console.error('❌ Download failed:', downloadError);
          alert('GIF was created but download failed. Please try again.');
          setIsExporting(false);
          setExportProgress(0);
        }
      });
      
      gif.on('progress', (progress) => {
        // Manual progress tracking seems more reliable
        const renderProgress = 85 + Math.round(progress * 15); // 85-100%
        setExportProgress(renderProgress);
        console.log(`🔄 GIF render: ${renderProgress}%`);
      });
      
      gif.on('abort', () => {
        clearTimeout(renderTimeout);
        console.error('❌ GIF creation was aborted');
        setIsExporting(false);
        setExportProgress(0);
        alert('GIF creation was interrupted. Please try again.');
      });
      
      // Start GIF rendering
      gif.render();
      
    } catch (error) {
      console.error('❌ GIF export failed:', error);
      alert('GIF export failed. Please try again.');
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  // File handling
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    
    if (file && file.type.startsWith('video/')) {
      setIsUploadingFile(true);
      setSelectedFile(file);
      
      // Create video URL and validate it can load
      const videoUrl = URL.createObjectURL(file);
      const tempVideo = document.createElement('video');
      tempVideo.src = videoUrl;
      tempVideo.muted = true;
      
      tempVideo.onloadedmetadata = () => {
        // Video is valid and loaded successfully
        setIsUploadingFile(false);
        setCurrentStep(2);
        
        // Clean up temp video
        URL.revokeObjectURL(videoUrl);
      };
      
      tempVideo.onerror = () => {
        // Video failed to load
        setIsUploadingFile(false);
        setSelectedFile(null);
        URL.revokeObjectURL(videoUrl);
        alert('Error loading video. Please try a different file.');
      };
      
      // Start loading
      tempVideo.load();
    } else if (file) {
      alert('Please select a valid video file (MP4, WebM, MOV, etc.)');
    }
  };

  return (
    <>
      {/* Fixed Top Header - Replaces Navbar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-black/[0.08] shadow-sm">
        <div className="max-w-[920px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <motion.button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back to Admin
              </motion.button>
              <div>
                <h1 className="text-2xl font-['VT323'] text-black/60">Invisible Looper</h1>
                <p className="text-lg font-['VT323'] text-black/40">
                  {currentStep === 1 && "Step 1: Upload Video for Smart Loop Processing"}
                  {currentStep === 2 && "Step 2: Smart Boomerang with Enhancement Zone Cutoff"}
                  {currentStep === 3 && "Step 3: Smart Boomerang Results"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - With top padding to account for fixed header */}
      <div className="pt-28 space-y-4 max-w-[920px] mx-auto px-6">

      {/* Step 1: Upload */}
      {currentStep === 1 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-8 shadow-sm border border-black/[0.08]"
        >
          
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isUploadingFile 
                ? 'border-cyan-500 bg-cyan-50 cursor-not-allowed' 
                : 'border-gray-300 hover:border-gray-400 cursor-pointer'
            }`}
            onClick={() => !isUploadingFile && fileInputRef.current?.click()}
          >
            {isUploadingFile ? (
              <>
                <div className="inline-block w-12 h-12 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="font-['VT323'] text-lg text-cyan-600 mb-2">
                  Processing {selectedFile?.name}...
                </p>
                <p className="font-['VT323'] text-sm text-cyan-500">
                  Preparing video for analysis
                </p>
              </>
            ) : (
              <>
                <FaUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="font-['VT323'] text-lg text-black/60 mb-2">
                  {selectedFile ? selectedFile.name : 'Click to upload video'}
                </p>
                <p className="font-['VT323'] text-sm text-black/40">
                  Best results with 5-30 second videos containing some still moments
                </p>
              </>
            )}
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            disabled={isUploadingFile}
            className="hidden"
          />
        </motion.div>
      )}

      {/* Step 2: Analysis & Settings */}
      {currentStep === 2 && selectedFile && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-8 shadow-sm border border-black/[0.08]"
        >
          
          {/* Video Preview */}
          <div className="mb-6">
            <div className={`w-full mx-auto relative transition-all duration-300 ${
              isVideoExpanded ? 'max-w-4xl' : 'max-w-lg'
            }`}>
              {/* Always render video element so ref can attach */}
              <video
                ref={videoRef}
                className={`w-full rounded-lg shadow-lg cursor-pointer transition-all duration-300 ${
                  isVideoLoading || videoLoadError ? 'hidden' : 'block'
                }`}
                controls
                muted
                playsInline
                preload="metadata"
                onClick={() => setIsVideoExpanded(!isVideoExpanded)}
                title={isVideoExpanded ? "Click to shrink video" : "Click to expand video"}
              />
              
              {/* Processing Overlay - Directly over video */}
              {isLoading && (
                <div className="absolute inset-0 bg-black/80 backdrop-blur-sm rounded-lg flex items-center justify-center z-20">
                  <div className="text-center max-w-sm mx-auto p-6">
                    {/* Processing Animation */}
                    <div className="relative w-12 h-12 mx-auto mb-4">
                      <div className="absolute inset-0 border-3 border-white/30 rounded-full"></div>
                      <div className="absolute inset-0 border-3 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    
                    {/* Processing Stage */}
                    <h3 className="font-['VT323'] text-lg text-white mb-2">
                      Creating Boomerang Effect
                    </h3>
                    <p className="font-['VT323'] text-cyan-300 mb-4 text-sm">
                      {processingStage}
                    </p>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-white/20 rounded-full h-2 mb-2">
                      <div 
                        className="bg-cyan-400 h-2 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${processingProgress}%` }}
                      ></div>
                    </div>
                    <p className="font-['VT323'] text-xs text-cyan-300">
                      {processingProgress.toFixed(1)}% complete
                    </p>
                  </div>
                </div>
              )}
              

              
              {/* Loading overlay */}
              {isVideoLoading && (
                <div className="bg-gray-100 rounded-lg p-8 text-center">
                  <div className="inline-block w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="font-['VT323'] text-lg text-gray-600">Loading video...</p>
                </div>
              )}
              
              {/* Error overlay */}
              {videoLoadError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
                  <p className="font-['VT323'] text-lg text-red-600 mb-4">⚠️ {videoLoadError}</p>
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-['VT323'] transition-colors"
                  >
                    Try Another File
                  </button>
                </div>
              )}
              

            </div>
          </div>
          

          {/* Hidden canvases for processing */}
          <canvas ref={canvasRef} style={{ display: 'none' }} width="320" height="240" />
          <canvas ref={previewCanvasRef} style={{ display: 'none' }} width="960" height="960" />
          
          {/* Processing Controls */}
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setCurrentStep(1)}
              disabled={isLoading}
              className="px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[16px] text-black/60 hover:text-black/80 transition-colors disabled:opacity-50"
            >
              Back
            </button>
            
            <button
              onClick={processVideo}
              disabled={isLoading || isVideoLoading || videoLoadError || !videoRef.current}
              className="flex items-center gap-2 px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-['VT323'] transition-colors disabled:opacity-50"
            >
              <FaMagic />
              Create Smart Boomerang Loop
            </button>
          </div>
        </motion.div>
      )}

      {/* Step 3: Results */}
      {currentStep === 3 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 backdrop-blur-xl rounded-xl p-8 shadow-sm border border-black/[0.08]"
                >
          
          {/* Video Preview */}
          <div className="mb-6">
            <div className={`w-full mx-auto relative transition-all duration-300 ${
              isVideoExpanded ? 'max-w-4xl' : 'max-w-lg'
            }`}>
              <canvas
                ref={previewCanvasRef}
                className="w-full rounded-lg shadow-lg bg-gray-100 cursor-pointer transition-all duration-300"
                width="960"
                height="960"
                style={{ 
                  display: 'block',
                  minHeight: '300px',
                  backgroundColor: '#f5f5f5'
                }}
                onClick={() => setIsVideoExpanded(!isVideoExpanded)}
                title={isVideoExpanded ? "Click to shrink video" : "Click to expand video"}
              />
              
              {/* Timeline Indicator */}
              {finalVideo && finalVideo.length > 0 && (
                <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-['VT323'] text-gray-800 mb-2">Timeline</h4>
                  
                  {/* Timeline Track */}
                  <div className="relative w-full h-4 bg-gray-200 rounded-full mb-2">
                    {/* Calculate the active duration (what we kept) vs total */}
                    {(() => {
                      const videoDuration = videoRef.current?.duration || 5;
                      const lastEnhancementZone = stillSegments.length > 0 ? 
                        stillSegments.reduce((latest, current) => current.end > latest.end ? current : latest) : null;
                      const activeDuration = lastEnhancementZone ? (lastEnhancementZone.end + 0.5) : (videoDuration * 0.95);
                      const cutoffPercentage = (activeDuration / videoDuration) * 100;
                      
                      return (
                        <>
                          {/* Kept Content Area */}
                          <div
                            className="absolute h-full bg-blue-300 rounded-l-full"
                            style={{
                              left: '0%',
                              width: `${cutoffPercentage}%`
                            }}
                            title={`Kept content: 0s - ${activeDuration.toFixed(1)}s`}
                          />
                          
                          {/* Trimmed Content Area */}
                          {cutoffPercentage < 100 && (
                            <div
                              className="absolute h-full bg-red-200 rounded-r-full"
                              style={{
                                left: `${cutoffPercentage}%`,
                                width: `${100 - cutoffPercentage}%`
                              }}
                              title={`Trimmed content: ${activeDuration.toFixed(1)}s - ${videoDuration.toFixed(1)}s`}
                            />
                          )}
                          
                          {/* Enhancement Zones */}
                          {stillSegments.length > 0 && stillSegments.map((segment, index) => (
                            <div
                              key={index}
                              className="absolute h-full bg-green-400 rounded-full opacity-80"
                              style={{
                                left: `${(segment.start / videoDuration) * 100}%`,
                                width: `${((segment.end - segment.start) / videoDuration) * 100}%`
                              }}
                              title={`Enhancement Zone ${index + 1}: ${segment.start.toFixed(1)}s - ${segment.end.toFixed(1)}s`}
                            />
                          ))}
                          
                          {/* Cutoff Line */}
                          <div
                            className="absolute top-0 h-full w-0.5 bg-gray-600"
                            style={{ left: `${cutoffPercentage}%` }}
                            title={`Cutoff at ${activeDuration.toFixed(1)}s`}
                          />
                          
                          {/* Playback Progress Indicator - Thin Line */}
                          <div 
                            className="absolute top-0 h-full w-0.5 bg-purple-600 transition-all duration-100"
                            style={{ 
                              left: `${Math.min(cutoffPercentage - 0.1, Math.max(0.1, (currentPlaybackTime / videoDuration) * 100))}%`
                            }}
                          />
                        </>
                      );
                    })()}
                  </div>
                  
                  {/* Timeline Labels */}
                  {(() => {
                    const videoDuration = videoRef.current?.duration || 5;
                    const lastEnhancementZone = stillSegments.length > 0 ? 
                      stillSegments.reduce((latest, current) => current.end > latest.end ? current : latest) : null;
                    const activeDuration = lastEnhancementZone ? (lastEnhancementZone.end + 0.5) : (videoDuration * 0.95);
                    
                    return (
                      <>
                        <div className="flex justify-center items-center relative text-xs font-['VT323'] text-gray-500">
                          <span className="text-purple-600">
                            {currentPlaybackTime.toFixed(1)}s
                          </span>
                          <span className="absolute right-0 text-gray-600">
                            {videoDuration.toFixed(1)}s
                          </span>
                        </div>
                        
                        {/* Simple Legend */}
                        <div className="mt-3 flex justify-center gap-6 text-xs font-['VT323']">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-blue-300 rounded"></div>
                            <span className="text-blue-700">Kept Content</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-green-400 rounded"></div>
                            <span className="text-green-700">Enhanced Zones</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-red-200 rounded"></div>
                            <span className="text-red-700">Trimmed Out</span>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
          
          {/* Playback Controls */}
          {finalVideo && (
            <div className="flex justify-center gap-4 mb-6">
              <button
                onClick={isPlaying ? stopGeneratedVideo : () => {
                  if (finalVideo && finalVideo.length > 0) {
                    playGeneratedVideo();
                  } else if (processedFrames && processedFrames.length > 0) {
                    renderAndPlayPreview(processedFrames);
                  } else {
                    alert('No video data available. Please process a video first.');
                  }
                }}
                className="flex items-center gap-2 px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-['VT323'] transition-colors"
              >
                {isPlaying ? <FaPause /> : <FaPlay />}
                {isPlaying ? 'Stop Loop' : 'Play Boomerang Loop'}
              </button>
              
              <button
                onClick={() => setShowExportModal(true)}
                className="flex items-center gap-2 px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-['VT323'] transition-colors"
              >
                <FaDownload />
                Export Loop
              </button>
            </div>
          )}
          

          
          {/* Hidden video element for step 3 (needed for manual playback fallbacks) */}
          {selectedFile && (
            <video
              ref={videoRef}
              style={{ display: 'none' }}
              src={URL.createObjectURL(selectedFile)}
              muted
              playsInline
            />
          )}

          {/* Navigation */}
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                setCurrentStep(2);
                setIsVideoExpanded(false);
              }}
              className="px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[16px] text-black/60 hover:text-black/80 transition-colors"
            >
              Back to Settings
            </button>
            
            <button
              onClick={() => {
                // Stop any playing video first
                if (isPlaying) {
                  stopGeneratedVideo();
                }
                
                // Reset to step 1 and clear all data
                setCurrentStep(1);
                setSelectedFile(null);
                setProcessedFrames([]);
                setFinalVideo(null);
                setMotionAnalysis([]);
                setStillSegments([]);
                setIsPlaying(false);
                setCurrentPlaybackTime(0);
                setIsVideoExpanded(false);
                setShowExportModal(false);
                setIsExporting(false);
                setExportProgress(0);
                
                // Clean up any running playback
                if (window.currentPlaybackStop) {
                  window.currentPlaybackStop();
                  window.currentPlaybackStop = null;
                }
              }}
              className="px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-['VT323'] transition-colors"
            >
              New Video
            </button>
          </div>
        </motion.div>
      )}

      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100]">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl"
          >
            <h3 className="font-['VT323'] text-2xl text-black/80 mb-6">Export Your Loop</h3>
            
            {/* Export Progress */}
            {isExporting && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-['VT323'] text-blue-800">Creating GIF...</span>
                  <span className="font-['VT323'] text-blue-600">{exportProgress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${exportProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              {/* GIF Export Option */}
              <button
                onClick={exportAsGIF}
                disabled={isExporting}
                className="w-full flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors disabled:opacity-50"
              >
                <div className="text-left">
                  <div className="font-['VT323'] text-lg text-purple-800">Animated GIF</div>
                  <div className="font-['VT323'] text-sm text-purple-600">Perfect for sharing on social media</div>
                </div>
                <div className="text-purple-600">
                  {isExporting ? (
                    <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <FaDownload className="w-5 h-5" />
                  )}
                </div>
              </button>

              {/* Coming Soon Options */}
              <div className="opacity-50 pointer-events-none">
                <div className="w-full flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="text-left">
                    <div className="font-['VT323'] text-lg text-gray-600">MP4 Video</div>
                    <div className="font-['VT323'] text-sm text-gray-500">Coming soon</div>
                  </div>
                  <FaDownload className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              <div className="opacity-50 pointer-events-none">
                <div className="w-full flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="text-left">
                    <div className="font-['VT323'] text-lg text-gray-600">WebM Video</div>
                    <div className="font-['VT323'] text-sm text-gray-500">Coming soon</div>
                  </div>
                  <FaDownload className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={() => {
                  setShowExportModal(false);
                  setIsExporting(false);
                  setExportProgress(0);
                }}
                disabled={isExporting}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-['VT323'] transition-colors disabled:opacity-50"
              >
                {isExporting ? 'Exporting...' : 'Cancel'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default InvisibleLooper; 